import type { LucideIcon } from 'lucide-react';
import { matchPath } from 'react-router';

import { appRouteRegistry, type AppRouteDefinition } from '@/app/route-registry';

export interface NavItem {
  title: string;
  icon: LucideIcon;
  to: string;
}

export interface NavSection {
  heading: string;
  items: NavItem[];
}

export interface Breadcrumb {
  section: string;
  item: string;
  child?: string;
}

/** Derive sidebar sections from the same available routes mounted by App. */
export function createNavigation(routes: readonly AppRouteDefinition[]): NavSection[] {
  const sections = new Map<string, NavItem[]>();

  for (const route of routes) {
    if (!route.navigation) continue;
    const items = sections.get(route.navigation.section) ?? [];
    items.push({ title: route.title, icon: route.icon, to: route.path });
    sections.set(route.navigation.section, items);
  }

  return Array.from(sections, ([heading, items]) => ({ heading, items }));
}

/** Keep the header concise by reusing the first available routes. */
export function createTopNavigation(routes: readonly AppRouteDefinition[]): NavItem[] {
  return routes
    .filter((route) => route.navigation)
    .sort((left, right) =>
      (left.navigation?.order ?? Number.MAX_SAFE_INTEGER) -
      (right.navigation?.order ?? Number.MAX_SAFE_INTEGER)
    )
    .map((route) => ({ title: route.title, icon: route.icon, to: route.path }));
}

/** Resolve exact, nested, and parameterized paths from route metadata. */
export function findBreadcrumb(pathname: string): Breadcrumb | undefined {
  const exactRoute = appRouteRegistry.find((candidate) => candidate.path === pathname);
  const nestedRoute = [...appRouteRegistry]
    .filter((candidate) => candidate.path !== '/')
    .sort((left, right) => right.path.length - left.path.length)
    .find((candidate) => matchPath({ path: candidate.path, end: false }, pathname));
  const route = exactRoute ?? nestedRoute;

  if (!route?.navigation) return undefined;
  return { section: route.navigation.section, item: route.title };
}

// Legacy demo pages remain available as source examples but are not mounted.
// Re-export their icons so those standalone files continue to typecheck.
export {
  ActivityIcon,
  BoxesIcon,
  ChartBarIcon,
  FileIcon,
  FileTextIcon,
  LayoutDashboardIcon,
  PackageIcon,
  SettingsIcon,
  ShieldIcon,
  SparklesIcon,
  UploadIcon
} from 'lucide-react';
